import { useReducer, useCallback } from 'react';
import { gcAccessToken, gcEnvironment, fetchWrap, getClientOrg } from '../../components/common/utilities/fetchWrap';
import config from '../../config';
import { reportConsole } from '../../components/common/utilities/logging-utilities';

// Interfaces
export interface FileTypeOption {
  id: string;
  label: string;
}

export interface SharePointConnection {
  connectionId: string;
  connectionName: string;
  baseUrl: string;
  configStatus?: string;
  lastValidated?: string;
  taggedKBCount: number;
  fileInclusions: string[];
  spTenantId?: string;
  clientId?: string;
  clientSecret?: string;
  authMethod?: string;
  siteName?: string;
}

export interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  connectionName: string;
}

export interface FormValidationErrors {
  connectionName?: string;
  spTenantId?: string;
  baseUrl?: string;
  content?: string;
}

export interface State {
  connections: SharePointConnection[];
  showAddForm: boolean;
  testing: boolean;
  saving: boolean;
  loading: boolean;
  verifyingTenant: boolean;
  newConnection: SharePointConnection;
  isEditing: boolean;
  validatingConnectionName: string;
  isClientReadOnly: boolean;
  isDeleteModalOpen: boolean;
  connToDelete: { name: string; id: string } | null;
  deletingConnectionId: string;
  validationErrors: FormValidationErrors;
  apiError: string | null;
  clientCredentialsChanged: boolean;
}

export type Action =
  | { type: 'SET_CONNECTIONS'; payload: SharePointConnection[] }
  | { type: 'TOGGLE_ADD_FORM' }
  | { type: 'SET_TESTING'; payload: boolean }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_VERIFYING_TENANT'; payload: boolean }
  | { type: 'SET_NEW_CONNECTION'; payload: Partial<SharePointConnection> }
  | { type: 'SET_IS_EDITING'; payload: boolean }
  | { type: 'SET_VALIDATING_CONNECTION_NAME'; payload: string }
  | { type: 'SET_CLIENT_READ_ONLY'; payload: boolean }
  | { type: 'SET_DELETE_MODAL'; payload: { isOpen: boolean; connToDelete: { name: string; id: string } | null } }
  | { type: 'SET_DELETING_CONNECTION_ID'; payload: string }
  | { type: 'SET_VALIDATION_ERRORS'; payload: FormValidationErrors }
  | { type: 'SET_API_ERROR'; payload: string | null }
  | { type: 'SET_CLIENT_CREDENTIALS_CHANGED'; payload: boolean };

// Constants
export const devBackendAPI = config.endpoints.endpoint || '';
export const fileTypeOptions: FileTypeOption[] = [
  { id: 'docx', label: 'Word (DOCX)' },
  { id: 'doc', label: 'Word Legacy (DOC)' },
  { id: 'pdf', label: 'PDF' },
  { id: 'txt', label: 'Text' },
  { id: 'html', label: 'HTML' },
  { id: 'aspx', label: 'ASPX' },
];

export const initialConnection: SharePointConnection = {
  connectionId: '',
  connectionName: '',
  siteName: '',
  baseUrl: '',
  authMethod: 'App Registered Oauth',
  taggedKBCount: 0,
  spTenantId: '',
  clientId: '',
  clientSecret: '',
  fileInclusions: [],
  configStatus: '',
};

// Form Validation
export const validateField = (
  name: keyof Pick<SharePointConnection, 'connectionName' | 'spTenantId' | 'baseUrl'>,
  value: string | undefined
): string | undefined => {
  const trimmedValue = value?.trim() || '';
  switch (name) {
    case 'connectionName':
      if (!trimmedValue) return 'Connection name is required';
      if (trimmedValue.length > 50) return 'Connection name must not exceed 50 characters';
      break;
    case 'spTenantId':
      if (!trimmedValue) return 'SharePoint Tenant ID is required';
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!guidRegex.test(trimmedValue)) return 'Tenant ID must be a valid GUID (e.g., 123e4567-e89b-12d3-a456-426614174000)';
      break;
    case 'baseUrl':
      if (!trimmedValue) return 'SharePoint URL is required';
      try {
        new URL(trimmedValue);
        if (!trimmedValue.toLowerCase().includes('sharepoint.com')) {
          return 'URL must be a valid SharePoint URL containing "sharepoint.com"';
        }
      } catch {
        return 'Please enter a valid URL (e.g., https://company.sharepoint.com/sites/example)';
      }
      break;
  }
};

// Custom Hooks
export const useApiRequest = (reducer: (state: State, action: Action) => State) => {
  const [state, dispatch] = useReducer(reducer, {
    connections: [],
    showAddForm: false,
    testing: false,
    saving: false,
    loading: true,
    verifyingTenant: false,
    newConnection: initialConnection,
    isEditing: false,
    validatingConnectionName: '',
    isClientReadOnly: false,
    isDeleteModalOpen: false,
    connToDelete: null,
    deletingConnectionId: '',
    validationErrors: {},
    apiError: null,
    clientCredentialsChanged: false
  });

  const executeRequest = useCallback(async <T,>(
    requestFn: () => Promise<T>,
    onSuccess?: (data: T) => void,
    onError?: (err: Error) => void
  ): Promise<T | null> => {
    dispatch({ type: 'SET_API_ERROR', payload: null });
    try {
      const result = await requestFn();
      reportConsole('API request successful', 'info', { result });
      if (onSuccess) onSuccess(result);
      return result;
    } catch (err: any) {
      const errorMessage = err.message || 'An error occurred';
      dispatch({ type: 'SET_API_ERROR', payload: errorMessage });
      if (onError) onError(err);
      reportConsole(err, 'error');
      return null;
    }
  }, []);

  return { state, dispatch, executeRequest };
};

export const useSharePointApi = () => {
  const getRequestHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    env: gcEnvironment(),
    token: gcAccessToken(),
    tokensource: 'purecloud'
  }), []);

  const fetchConnections = useCallback(async (): Promise<SharePointConnection[]> => {
    reportConsole('Fetching SharePoint connections', 'info');
    const response = await fetchWrap({
      url: `${devBackendAPI}/getSPConfiguration`,
      options: {
        method: 'POST',
        body: JSON.stringify({ tenantId: getClientOrg(), system: 'source' })
      },
      token: gcAccessToken(),
      auth: true,
      cookies: false,
      headerOptions: getRequestHeaders(),
      noContentType: false,
      contentType: 'application/json',
      noResponseData: false
    });
    reportConsole('Successfully fetched connections', 'info', { response });
    return response.data.results || [];
  }, [getRequestHeaders]);

  const verifyTenant = useCallback(async (spTenantId: string): Promise<any> => {
    reportConsole('Verifying tenant', 'info', { spTenantId });
    const response = await fetchWrap({
      url: `${devBackendAPI}/getClientCredentials`,
      options: {
        method: 'POST',
        body: JSON.stringify({ tenantId: getClientOrg(), system: 'source', spTenantId })
      },
      token: gcAccessToken(),
      auth: true,
      cookies: false,
      headerOptions: getRequestHeaders(),
      noContentType: false,
      contentType: 'application/json',
      noResponseData: false
    });
    return response.data;
  }, [getRequestHeaders]);

  const saveConnection = useCallback(async (nestedData: SharePointConnection, action: string, dbUpdateNeeded?: boolean): Promise<any> => {
    reportConsole('Saving connection', 'info', { nestedData, action });
    const response = await fetchWrap({
      url: `${devBackendAPI}/setSPConfiguration`,
      options: {
        method: 'POST',
        body: JSON.stringify({
          nestedData,
          action,
          tenantId: getClientOrg(),
          dbUpdateNeeded,
        })
      },
      token: gcAccessToken(),
      auth: true,
      cookies: false,
      headerOptions: getRequestHeaders(),
      noContentType: false,
      contentType: 'application/json',
      noResponseData: false
    });
    reportConsole('Successfully saved connection', 'info', { response });
    return response.data;
  }, [getRequestHeaders]);

  const deleteConnection = useCallback(async (connectionId: string, connectionName: string): Promise<any> => {
    reportConsole('Deleting connection', 'info', { connectionId });
    const response = await fetchWrap({
      url: `${devBackendAPI}/deleteSPConfiguration`,
      options: {
        method: 'DELETE',
        body: JSON.stringify({
          connectionId,
          connectionName,
          tenantId: getClientOrg()
        })
      },
      token: gcAccessToken(),
      auth: true,
      cookies: false,
      headerOptions: getRequestHeaders(),
      noContentType: false,
      contentType: 'application/json',
      noResponseData: false
    });
    reportConsole('Successfully deleted connection', 'info', { response });
    return response.data;
  }, [getRequestHeaders]);

  return { fetchConnections, verifyTenant, saveConnection, deleteConnection };
};
